export function getRectsIntersectionArea(
  a: DOMRectReadOnly,
  b: DOMRectReadOnly
) {
  const width = Math.max(
    0,
    Math.min(a.right, b.right) - Math.max(a.left, b.left)
  );
  const height = Math.max(
    0,
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  );
  return width * height;
}

export function getMobilePdfPageScrollTop(
  viewport: Element,
  pageEl: HTMLElement
) {
  const viewportRect = viewport.getBoundingClientRect();
  const pageRect = pageEl.getBoundingClientRect();
  return viewport.scrollTop + (pageRect.top - viewportRect.top);
}

export function isMobilePdfPageRendered(pageRoot?: HTMLElement | null) {
  if (!pageRoot) return false;
  const inner = pageRoot.querySelector('.media_viewer_pdf-inner');
  if (!inner) return false;
  return !!inner.querySelector('.react-pdf__Page__canvas');
}

export function scrollMobileViewportToFabricObjects(
  viewport: Element,
  canvasEl: HTMLCanvasElement | null,
  getObjectBounds: () => { top: number; bottom: number } | null,
  padding = 48
) {
  if (!canvasEl) return;
  const bounds = getObjectBounds();
  if (!bounds) return;

  const viewportRect = viewport.getBoundingClientRect();
  let scrollDelta = 0;

  if (bounds.top < viewportRect.top + padding) {
    scrollDelta = bounds.top - viewportRect.top - padding;
  } else if (bounds.bottom > viewportRect.bottom - padding) {
    scrollDelta = bounds.bottom - viewportRect.bottom + padding;
  }

  if (scrollDelta !== 0) {
    viewport.scrollTop += scrollDelta;
  }
}

export function pickMostVisiblePage(
  pages: { page: number }[],
  pageElements: Map<number, HTMLDivElement>,
  viewport: Element,
  minVisibleRatio = 0.35
) {
  const viewportRect = viewport.getBoundingClientRect();
  const minVisibleArea =
    viewportRect.width * viewportRect.height * minVisibleRatio;

  let bestPage = 0;
  let bestVisibleArea = 0;

  pages.forEach(({ page }) => {
    const element = pageElements.get(page);
    if (!element) return;

    const visibleArea = getRectsIntersectionArea(
      element.getBoundingClientRect(),
      viewportRect
    );

    if (visibleArea > bestVisibleArea) {
      bestVisibleArea = visibleArea;
      bestPage = page;
    }
  });

  if (!bestVisibleArea || bestVisibleArea < minVisibleArea) return null;
  return bestPage;
}

export function getTwoFingerSpan(touches: TouchList) {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

export function getInitialPdfPageScale(
  originalWidth: number,
  originalHeight: number,
  maxCanvasSize: number,
  maxZoom: number,
  isMobile: boolean
) {
  const maxDimension = Math.max(originalWidth, originalHeight);
  const canvasBudget = isMobile ? maxCanvasSize / maxZoom : maxCanvasSize;
  const scale = canvasBudget / maxDimension;
  return {
    width: originalWidth * scale,
    height: originalHeight * scale
  };
}

export function handleSelectionChange(
  textLayersMap: Map<HTMLDivElement, HTMLDivElement>,
  prevRange: Range | null
): Range | null {
  const selection = document.getSelection();
  const selectedText = selection?.toString?.()?.trim() || '';

  if (!selection || selection.rangeCount === 0 || !selectedText) {
    return null;
  }

  const activeTextLayers = new Set<HTMLDivElement>();

  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    for (const textLayerDiv of textLayersMap.keys()) {
      if (
        !activeTextLayers.has(textLayerDiv) &&
        range.intersectsNode(textLayerDiv)
      ) {
        activeTextLayers.add(textLayerDiv);
      }
    }
  }

  const range = selection.getRangeAt(0);

  const modifyStart =
    prevRange &&
    (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
      range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);

  let anchor: any = modifyStart ? range.startContainer : range.endContainer;
  if (anchor.nodeType === Node.TEXT_NODE) {
    anchor = anchor.parentNode;
  }

  if (!modifyStart && range.endOffset === 0) {
    do {
      while (!anchor.previousSibling) {
        anchor = anchor.parentNode;
      }
      anchor = anchor.previousSibling;
    } while (!anchor.childNodes.length);
  }

  const parentTextLayer = anchor.parentElement?.closest('.textLayer');
  if (!parentTextLayer) return null;

  const endDiv = textLayersMap.get(parentTextLayer as HTMLDivElement);

  if (
    endDiv instanceof HTMLDivElement &&
    parentTextLayer instanceof HTMLDivElement
  ) {
    endDiv.style.width = parentTextLayer.style.width;
    endDiv.style.height = parentTextLayer.style.height;
    anchor.parentElement?.insertBefore(
      endDiv,
      modifyStart ? anchor : anchor.nextSibling
    );
  }
  return range.cloneRange();
}
